/**
 * Site Detail — Phase 7 Sub-iter 7D.
 *
 * EntityShell + tabs migration.  Tabs: Overview / Work / Account /
 * Media / Equipment / Activity.  Status strip and breadcrumb live in
 * the body of the Overview tab (they're site-specific, so they don't
 * belong in the shell chrome).
 */
import { useEffect, useState, useCallback, useMemo } from "react";
import { useParams, Link, useSearchParams } from "react-router-dom";
import {
  MapPin, Navigation, Briefcase, Clock, LifeBuoy, CheckCircle2, AlertCircle,
} from "lucide-react";
import api from "../lib/api";
import { useAutoRefresh } from "../hooks/useAutoRefresh";
import { useAuth } from "../context/AuthContext";
import { EntityShell } from "../components/shell";
import HistoryBlock from "../components/shell/HistoryBlock";
import { usePersistedState } from "../lib/persisted";

import JobCardsBlock from "../components/customer-blocks/JobCardsBlock";
import TicketsBlock from "../components/customer-blocks/TicketsBlock";
import LeadsBlock from "../components/customer-blocks/LeadsBlock";
import CallsBlock from "../components/customer-blocks/CallsBlock";
import EquipmentBlock from "../components/customer-blocks/EquipmentBlock";
import ServiceTrackerBlock from "../components/customer-blocks/ServiceTrackerBlock";
import DocumentsBlock from "../components/customer-blocks/DocumentsBlock";
import GalleryBlock from "../components/customer-blocks/GalleryBlock";

export default function SiteDetail() {
  const { cid, sid } = useParams();
  const { hasPermission } = useAuth();
  // [Iter 290zp.22] Was: `["admin","manager","owner"].includes(user?.role)`
  // — hardcoded role check that bypassed the permission system. Sites
  // are part of the customer record, so they share the
  // `action.edit_customers` permission grant.
  const canEdit = hasPermission("action.edit_customers");

  const [activeTab, setActiveTab] = usePersistedState("site.activeTab", "overview");
  const [searchParams] = useSearchParams();
  const tabParam = searchParams.get("tab");
  useEffect(() => {
    if (tabParam) setActiveTab(tabParam);
  }, [tabParam, setActiveTab]);

  const [customer, setCustomer] = useState(null);
  const [site, setSite] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [leads, setLeads] = useState([]);
  const [calls, setCalls] = useState([]);
  const [equipment, setEquipment] = useState([]);
  const [serviceLogs, setServiceLogs] = useState([]);
  const [salesDocs, setSalesDocs] = useState([]);
  const [galleryFolders, setGalleryFolders] = useState([]);
  const [galleryItems, setGalleryItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    const [cr, sr, kr, jr, tr, lr, cl, er, slr, sd, gf, gi] = await Promise.all([
      api.get(`/customers/${cid}`),
      api.get(`/sites?customer_id=${cid}`),
      api.get(`/contacts?customer_id=${cid}`),
      api.get(`/jobcards?customer_id=${cid}`),
      api.get(`/tickets?customer_id=${cid}`),
      api.get(`/leads`),
      api.get(`/calls?customer_id=${cid}`),
      api.get(`/equipment?customer_id=${cid}`),
      api.get(`/service-logs?customer_id=${cid}`),
      api.get(`/sales-documents?customer_id=${cid}`),
      api.get(`/gallery-folders?customer_id=${cid}`),
      api.get(`/gallery-items?customer_id=${cid}`),
    ]);
    setCustomer(cr.data);
    setSite(sr.data.find((s) => s.id === sid) || null);
    setContacts(kr.data);
    setJobs(jr.data);
    setTickets(tr.data);
    setLeads(lr.data.filter((l) => l.customer_id === cid));
    setCalls(cl.data);
    setEquipment(er.data);
    setServiceLogs(slr.data);
    setSalesDocs(sd.data);
    setGalleryFolders(gf.data);
    setGalleryItems(gi.data);
    setLoading(false);
  }, [cid, sid]);

  useEffect(() => {
    setLoading(true);
    load();
  }, [load]);
  useAutoRefresh(load, { intervalMs: 45_000 });

  // ────────── site-scoped slices ──────────
  const bySite = (arr) => arr.filter((x) => x?.site_id === sid);
  const siteJobs = useMemo(() => bySite(jobs), [jobs, sid]);
  const siteTickets = useMemo(() => bySite(tickets), [tickets, sid]);
  const siteLeads = useMemo(() => bySite(leads), [leads, sid]);
  const siteEquipment = useMemo(() => bySite(equipment), [equipment, sid]);
  const siteServiceLogs = useMemo(() => {
    const eqIds = new Set(siteEquipment.map((e) => e.id));
    return serviceLogs.filter((s) => eqIds.has(s.site_equipment_id));
  }, [serviceLogs, siteEquipment]);
  const siteGalleryItems = useMemo(() => bySite(galleryItems), [galleryItems, sid]);
  const siteGalleryFolders = useMemo(() => bySite(galleryFolders), [galleryFolders, sid]);
  const siteCalls = useMemo(() => bySite(calls), [calls, sid]);

  // ────────── status strip stats ──────────
  const strip = useMemo(() => {
    const openJobs = siteJobs.filter((j) => !["complete", "paid", "closed", "cancelled"].includes(j.phase)).length;
    const openTickets = siteTickets.filter((t) => !["resolved", "closed", "cancelled"].includes(t.status)).length;
    const lastLog = [...siteServiceLogs].sort(
      (a, b) => new Date(b.service_date || b.created_at) - new Date(a.service_date || a.created_at),
    )[0];
    let lastServiceAgo = null;
    if (lastLog?.service_date || lastLog?.created_at) {
      const d = new Date(lastLog.service_date || lastLog.created_at);
      lastServiceAgo = Math.max(0, Math.floor((Date.now() - d.getTime()) / 86400000));
    }
    return { openJobs, openTickets, lastServiceAgo };
  }, [siteJobs, siteTickets, siteServiceLogs]);

  if (loading) {
    return <div className="p-6 text-sm text-[#647087]" data-testid="site-detail-loading">Loading site…</div>;
  }
  if (!customer || !site) {
    return (
      <div className="p-6 space-y-3" data-testid="site-detail-missing">
        <Link to={`/customers/${cid}`} className="hv-btn hv-btn-ghost text-xs">
          ← Back to customer
        </Link>
        <div className="hv-card p-6 text-center text-sm text-rose-300">
          {!customer ? "Customer not found." : "Site not found on this customer."}
        </div>
      </div>
    );
  }

  const mapQuery = site.lat && site.lng
    ? `${site.lat},${site.lng}`
    : encodeURIComponent(site.address || "");
  const mapUrl = `https://www.google.com/maps/search/?api=1&query=${mapQuery}`;

  const headerActions = (
    <a
      href={mapUrl}
      target="_blank"
      rel="noopener noreferrer"
      className="hv-btn hv-btn-primary text-xs"
      data-testid="site-navigate-hero"
    >
      <Navigation className="w-3.5 h-3.5" /> Navigate
    </a>
  );

  const tabs = [
    { key: "overview", label: "Overview" },
    { key: "work", label: "Work", count: (siteJobs.length + siteTickets.length) || undefined },
    { key: "account", label: "Account" },
    { key: "media", label: "Media", count: siteGalleryItems.length || undefined },
    { key: "equipment", label: "Equipment", count: siteEquipment.length || undefined },
    { key: "activity", label: "Activity" },
  ];

  return (
    <EntityShell
      kindLabel="Site"
      title={site.name}
      status={{
        label: site.is_active === false ? "inactive" : "active",
        tone: site.is_active === false ? "slate" : "green",
      }}
      customerLink={{
        label: `View customer · ${customer.name}`,
        to: `/customers/${cid}`,
      }}
      backTo={`/customers/${cid}`}
      actions={headerActions}
      tabs={tabs}
      activeTab={activeTab}
      onTabChange={setActiveTab}
    >
      <div className="space-y-4" data-testid="site-detail-page">
        {/* Status strip — site-specific stats, shown above the active tab content. */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2" data-testid="site-status-strip">
          <StatusPill
            icon={CheckCircle2}
            label={site.is_active === false ? "Inactive site" : "Active site"}
            tone={site.is_active === false ? "muted" : "ok"}
            testId="site-stat-active"
          />
          <StatusPill
            icon={Briefcase}
            label={`${strip.openJobs} open job card${strip.openJobs !== 1 ? "s" : ""}`}
            tone={strip.openJobs > 0 ? "attn" : "muted"}
            testId="site-stat-jobs"
          />
          <StatusPill
            icon={LifeBuoy}
            label={`${strip.openTickets} open ticket${strip.openTickets !== 1 ? "s" : ""}`}
            tone={strip.openTickets > 0 ? "warn" : "muted"}
            testId="site-stat-tickets"
          />
          <StatusPill
            icon={Clock}
            label={strip.lastServiceAgo == null ? "No service yet" : `Last service ${strip.lastServiceAgo}d ago`}
            tone={strip.lastServiceAgo == null ? "muted" : strip.lastServiceAgo > 180 ? "warn" : "ok"}
            testId="site-stat-last-service"
          />
        </div>

        {activeTab === "overview" && (
          <>
            <div className="hv-card p-4 space-y-3" data-testid="site-overview-info">
              <h3 className="font-display font-bold">Site info</h3>
              {site.address && (
                <div className="text-sm text-[#a0aabf] flex items-start gap-1">
                  <MapPin className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                  <span className="break-words">{site.address}</span>
                </div>
              )}
              <div className="grid md:grid-cols-2 gap-x-6 gap-y-2 text-sm">
                <KV label="Coordinates" value={site.lat && site.lng ? `${site.lat}, ${site.lng}` : "—"} />
                <KV label="Site ID" value={<span className="font-mono text-xs">{site.id}</span>} />
                <KV label="Status" value={site.is_active === false ? "Inactive" : "Active"} />
              </div>
              {site.notes && (
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-[#647087]">Notes</div>
                  <div className="text-sm text-[#dde3ec] whitespace-pre-wrap">{site.notes}</div>
                </div>
              )}
            </div>
            <LeadsBlock leads={siteLeads} canEdit={canEdit} onChanged={load} />
          </>
        )}

        {activeTab === "work" && (
          <>
            <JobCardsBlock jobs={siteJobs} sites={[site]} canEdit={canEdit} onChanged={load} previewEditMode={canEdit} />
            <TicketsBlock tickets={siteTickets} canEdit={canEdit} onChanged={load} previewEditMode={canEdit} />
          </>
        )}

        {activeTab === "account" && (
          <>
            <div className="hv-card p-3 text-xs text-[#a0aabf] flex items-center gap-2" data-testid="site-account-note">
              <AlertCircle className="w-3.5 h-3.5 text-amber-300 shrink-0" />
              <span>
                Statements and account-wide settings live on the{" "}
                <Link to={`/customers/${cid}`} className="text-[#1fb6c1] hover:underline">customer record</Link>.
                Docs below are filtered to scans / OCR jobs referencing this site.
              </span>
            </div>
            <DocumentsBlock customer={customer} canEdit={canEdit} salesDocs={salesDocs} onChanged={load} siteId={sid} />
          </>
        )}

        {activeTab === "media" && (
          <GalleryBlock
            customerId={cid}
            folders={siteGalleryFolders}
            items={siteGalleryItems}
            jobcards={siteJobs}
            tickets={siteTickets}
            canEdit={canEdit}
            canUpload={canEdit}
            canDelete={canEdit}
            onChanged={load}
            defaultSiteId={sid}
          />
        )}

        {activeTab === "equipment" && (
          <>
            <EquipmentBlock
              equipment={siteEquipment}
              serviceLogs={siteServiceLogs}
              sites={[site]}
              canEdit={canEdit}
              onChanged={load}
              presetSiteId={sid}
              customerId={site?.customer_id}
              defaultOpen
            />
            <ServiceTrackerBlock
              equipment={siteEquipment}
              serviceLogs={siteServiceLogs}
              sites={[site]}
              jobcards={siteJobs}
              canEdit={canEdit}
              onChanged={load}
            />
          </>
        )}

        {activeTab === "activity" && (
          <>
            <CallsBlock
              customerId={cid}
              calls={siteCalls}
              contacts={contacts}
              leads={siteLeads}
              jobcards={siteJobs}
              tickets={siteTickets}
              onChanged={load}
            />
            <HistoryBlock entity={{ id: sid, kind: "site" }} />
          </>
        )}
      </div>
    </EntityShell>
  );
}

/* ─────────────────────── helpers ─────────────────────── */

function StatusPill({ icon: Icon, label, tone = "muted", testId }) {
  const toneCls = {
    ok: "border-emerald-500/40 bg-emerald-500/5 text-emerald-300",
    warn: "border-amber-500/40 bg-amber-500/5 text-amber-300",
    attn: "border-[#1fb6c1]/40 bg-[#1fb6c1]/5 text-[#1fb6c1]",
    muted: "border-white/10 bg-white/[0.02] text-[#a0aabf]",
  }[tone];
  return (
    <div className={`rounded-md px-3 py-2 border text-xs flex items-center gap-2 ${toneCls}`} data-testid={testId}>
      <Icon className="w-3.5 h-3.5 shrink-0" />
      <span className="truncate">{label}</span>
    </div>
  );
}

function KV({ label, value }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-[#647087]">{label}</div>
      <div className="text-sm text-[#dde3ec]">{value}</div>
    </div>
  );
}
